import { Type, Static } from '@sinclair/typebox'
import omit from 'lodash.omit'

import { DAIRY_CLASSES } from '@ruminati/constants/gaf/dairy'

import { OptionalNullableCleanString } from './utils/shared-types'
import { SeasonTB, NumberMinZeroDefaultUndefinedTB, OtherNFertilisersTypeTB, CattleTransportTruckTypeTB } from './report_shared'

export const DairyProductionSystemTypeIrrigatedPastureTB = Type.Literal('irrigated-pasture')
export const DairyProductionSystemTypeNonIrrigatedPastureTB = Type.Literal('non-irrigated-pasture')
export const DairyProductionSystemTypeIrrigatedCropTB = Type.Literal('irrigated-crop')
export const DairyProductionSystemTypeNonIrrigatedCropTB = Type.Literal('non-irrigated-crop')

export const DairyProductionSystemTypeTB = Type.Union([
  DairyProductionSystemTypeIrrigatedPastureTB,
  DairyProductionSystemTypeNonIrrigatedPastureTB,
  DairyProductionSystemTypeIrrigatedCropTB,
  DairyProductionSystemTypeNonIrrigatedCropTB
])
export type DairyProductionSystemTypeT = Static<typeof DairyProductionSystemTypeTB>

export const DairyProductionSystemTypeMapTB = Type.Object({
  'Irrigated Pasture': DairyProductionSystemTypeIrrigatedPastureTB,
  'Dryland Pasture': DairyProductionSystemTypeNonIrrigatedPastureTB,
  'Irrigated Crop': DairyProductionSystemTypeIrrigatedCropTB,
  'Dryland Crop': DairyProductionSystemTypeNonIrrigatedCropTB
})
export type DairyProductionSystemTypeMapT = Static<typeof DairyProductionSystemTypeMapTB>

export const DairyCattleClassIdTB = Type.Union(
  DAIRY_CLASSES.map(c => Type.Literal(c.value)),
)
export type DairyCattleClassIdT = Static<typeof DairyCattleClassIdTB>

export const DairyCattleTB = Type.Array(Type.Object({
  classId: DairyCattleClassIdTB,
  name: OptionalNullableCleanString,
  headcount: SeasonTB({}),
  liveweight: SeasonTB({ maximum: 1500 }),
  liveweightGain: SeasonTB({ maximum: 4, minimum: -4 }),
  crudeProteinIntake: SeasonTB({ minimum: 5, maximum: 25 }),
  dryMatterDigestability: SeasonTB({ minimum: 50, maximum: 90 }),
}), { minItems: 1, default: [] })
export type DairyCattleT = Static<typeof DairyCattleTB>

export const DairyProductionSystemTB = Type.Object({
  area: NumberMinZeroDefaultUndefinedTB(),
  nFertiliser: NumberMinZeroDefaultUndefinedTB(),
  urea: NumberMinZeroDefaultUndefinedTB(),
})
export type DairyProductionSystemT = Static<typeof DairyProductionSystemTB>

// capitalised for historic data reasons
export const DairyManureManagementTypePastureTB = Type.Literal('Pasture')
export const DairyManureManagementTypeLagoonTB = Type.Literal('Lagoon')
export const DairyManureManagementTypeSumpDispersalTB = Type.Literal('SumpDispersal')
export const DairyManureManagementTypeDrainToPaddockTB = Type.Literal('DrainToPaddock')
export const DairyManureManagementTypeSolidStorageTB = Type.Literal('SolidStorage')

export const DairyManureManagementTypeTB = Type.Union([
  DairyManureManagementTypePastureTB,
  DairyManureManagementTypeLagoonTB,
  DairyManureManagementTypeSumpDispersalTB,
  DairyManureManagementTypeDrainToPaddockTB,
  DairyManureManagementTypeSolidStorageTB
])
export type DairyManureManagementTypesT = Static<typeof DairyManureManagementTypeTB>

export const DairyManureManagementTypeMapTB = Type.Object({
  'Pasture': DairyManureManagementTypePastureTB,
  'Anaerobic Lagoon': DairyManureManagementTypeLagoonTB,
  'Sump & Dispersal': DairyManureManagementTypeSumpDispersalTB,
  'Drain to Paddocks': DairyManureManagementTypeDrainToPaddockTB,
  'Solid Storage': DairyManureManagementTypeSolidStorageTB,
})
export type DairyManureManagementTypeMapT = Static<typeof DairyManureManagementTypeMapTB>

export const DairyManureManagementComponentTB = Type.Object({
  Pasture: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  Lagoon: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  SumpDispersal: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  DrainToPaddock: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  SolidStorage: NumberMinZeroDefaultUndefinedTB({ maximum: 100 })
})
export type DairyManureManagementComponentT = Static<typeof DairyManureManagementComponentTB>

export const DairyManureManagementFormTB = Type.Object({
  milkingCows: DairyManureManagementComponentTB,
  otherCows: DairyManureManagementComponentTB
})
export type DairyManureManagementFormT = Static<typeof DairyManureManagementFormTB>

export const DairyManureManagementRecordTB = Type.Object({
  milkingPasture: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  milkingLagoon: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  milkingSumpDispersal: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  milkingDrainToPaddock: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  milkingSolidStorage: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  otherPasture: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  otherLagoon: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  otherSumpDispersal: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  otherDrainToPaddock: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  otherSolidStorage: NumberMinZeroDefaultUndefinedTB({ maximum: 100 })
})
export type DairyManureManagementRecordT = Static<typeof DairyManureManagementRecordTB>

// transform functions from db -> client form and client form -> db
export function transformDairyManureManagementRecordToForm(record: DairyManureManagementRecordT): DairyManureManagementFormT {
  return {
    milkingCows: {
      Pasture: record.milkingPasture * 100,
      Lagoon: record.milkingLagoon * 100,
      SumpDispersal: record.milkingSumpDispersal * 100,
      DrainToPaddock: record.milkingDrainToPaddock * 100,
      SolidStorage: record.milkingSolidStorage * 100,
    },
    otherCows: {
      Pasture: record.otherPasture * 100,
      Lagoon: record.otherLagoon * 100,
      SumpDispersal: record.otherSumpDispersal * 100,
      DrainToPaddock: record.otherDrainToPaddock * 100,
      SolidStorage: record.otherSolidStorage * 100,
    },
  };
}
export function transformDairyManureManagementFormToRecord(record: DairyManureManagementFormT): DairyManureManagementRecordT {
  return {
    milkingPasture: record.milkingCows.Pasture / 100,
    milkingLagoon: record.milkingCows.Lagoon / 100,
    milkingSumpDispersal: record.milkingCows.SumpDispersal / 100,
    milkingDrainToPaddock: record.milkingCows.DrainToPaddock / 100,
    milkingSolidStorage: record.milkingCows.SolidStorage / 100,
    otherPasture: record.otherCows.Pasture / 100,
    otherLagoon: record.otherCows.Lagoon / 100,
    otherSumpDispersal: record.otherCows.SumpDispersal / 100,
    otherDrainToPaddock: record.otherCows.DrainToPaddock / 100,
    otherSolidStorage: record.otherCows.SolidStorage / 100,
  };
}

export const DairyProductionSystemFormTB = Type.Object({
  limestone: NumberMinZeroDefaultUndefinedTB(),
  singleSuperphosphate: NumberMinZeroDefaultUndefinedTB(),
  herbicide: NumberMinZeroDefaultUndefinedTB(),
  herbicidePesticide: NumberMinZeroDefaultUndefinedTB(),
  primaryProductionSystemInputs: DairyProductionSystemTB,
  otherProductionSystemInputs: Type.Optional(Type.Array(
    Type.Composite([
      DairyProductionSystemTB,
      // N.B. we don't send the type field to the API, but need to know in the UI which additional systems were selected to facilitate error handling etc (user must select a type to inform data that _does_ go to API, see below)
      Type.Object({
        type: DairyProductionSystemTypeTB,
      })
    ]),
    { maxItems: 3, default: [] }
  )),
  otherNFertilisers: Type.Optional(Type.Array(Type.Object({
    type: OtherNFertilisersTypeTB,
    amount: NumberMinZeroDefaultUndefinedTB()
  })))
})
export type DairyProductionSystemFormT = Static<typeof DairyProductionSystemFormTB>

export const DairyProductionSystemRecordTB = Type.Object({
  limestone: NumberMinZeroDefaultUndefinedTB(),
  singleSuperphosphate: NumberMinZeroDefaultUndefinedTB(),
  herbicide: NumberMinZeroDefaultUndefinedTB(),
  herbicidePesticide: NumberMinZeroDefaultUndefinedTB(),
  // N.B. properties of irrigated and dryland are zeroed-out if the user doesn't provide them, as the GAF algorithm's expect these to have values
  irrigated: Type.Object({
    areaCropped: Type.Number(),
    areaImprovedPasture: Type.Number(),
    nFertiliserCrops: Type.Number(),
    nFertiliserPasture: Type.Number(),
    ureaFertiliserCrops: Type.Number(),
    ureaFertiliserPasture: Type.Number()
  }),
  dryland: Type.Object({
    areaCropped: Type.Number(),
    areaImprovedPasture: Type.Number(),
    nFertiliserCrops: Type.Number(),
    nFertiliserPasture: Type.Number(),
    ureaFertiliserCrops: Type.Number(),
    ureaFertiliserPasture: Type.Number()
  }),
  // TODO: non-null in db, probably should be nullable if users don't have to include other fertilisers?
  otherNFertilisers: Type.Array(Type.Object({
    type: OtherNFertilisersTypeTB,
    amount: NumberMinZeroDefaultUndefinedTB()
  }))
})
export type DairyProductionSystemRecordT = Static<typeof DairyProductionSystemRecordTB>

// transform functions from db -> client form and client form -> db
// N.B. we don't use TypeBox Type.Transform + Decode + Encode, as those are really only a value-add in DX when the decoding and encoding is for the same TB type... i.e. when the transforming is really about converting to appropriate data types to send across HTTP. In our case, we transform to different data structures in both directions.
// TODO: could have unit tests for these functions?
export function transformDairyProductionSystemRecordToForm(record: DairyProductionSystemRecordT, primaryProductionSystemType: DairyProductionSystemTypeT): DairyProductionSystemFormT {
  const omittedKeys = ['dryland', 'irrigated'] as const
  return {
    ...omit(record, omittedKeys),
    primaryProductionSystemInputs: {
      area: primaryProductionSystemType === DairyProductionSystemTypeNonIrrigatedPastureTB.const
        ? record.dryland.areaImprovedPasture
        : primaryProductionSystemType === DairyProductionSystemTypeNonIrrigatedCropTB.const
          ? record.dryland.areaCropped
          : primaryProductionSystemType === DairyProductionSystemTypeIrrigatedPastureTB.const
            ? record.irrigated.areaImprovedPasture
            : record.irrigated.areaCropped,
      nFertiliser: primaryProductionSystemType === DairyProductionSystemTypeNonIrrigatedPastureTB.const
        ? record.dryland.nFertiliserPasture
        : primaryProductionSystemType === DairyProductionSystemTypeNonIrrigatedCropTB.const
          ? record.dryland.nFertiliserCrops
          : primaryProductionSystemType === DairyProductionSystemTypeIrrigatedPastureTB.const
            ? record.irrigated.nFertiliserPasture
            : record.irrigated.nFertiliserCrops,
      urea: primaryProductionSystemType === DairyProductionSystemTypeNonIrrigatedPastureTB.const
        ? record.dryland.ureaFertiliserPasture
        : primaryProductionSystemType === DairyProductionSystemTypeNonIrrigatedCropTB.const
          ? record.dryland.ureaFertiliserCrops
          : primaryProductionSystemType === DairyProductionSystemTypeIrrigatedPastureTB.const
            ? record.irrigated.ureaFertiliserPasture
            : record.irrigated.ureaFertiliserCrops,
    },
    otherProductionSystemInputs: [
      ...((record.dryland.areaImprovedPasture > 0 || record.dryland.nFertiliserPasture > 0 || record.dryland.ureaFertiliserPasture > 0) && primaryProductionSystemType !== DairyProductionSystemTypeNonIrrigatedPastureTB.const ? [{ area: record.dryland.areaImprovedPasture, nFertiliser: record.dryland.nFertiliserPasture, urea: record.dryland.ureaFertiliserPasture, type: DairyProductionSystemTypeNonIrrigatedPastureTB.const }] : []),
      ...((record.dryland.areaCropped > 0 || record.dryland.nFertiliserCrops > 0 || record.dryland.ureaFertiliserCrops > 0) && primaryProductionSystemType !== DairyProductionSystemTypeNonIrrigatedCropTB.const ? [{ area: record.dryland.areaCropped, nFertiliser: record.dryland.nFertiliserCrops, urea: record.dryland.ureaFertiliserCrops, type: DairyProductionSystemTypeNonIrrigatedCropTB.const }] : []),
      ...((record.irrigated.areaImprovedPasture > 0 || record.irrigated.nFertiliserPasture > 0 || record.irrigated.ureaFertiliserPasture > 0) && primaryProductionSystemType !== DairyProductionSystemTypeIrrigatedPastureTB.const ? [{ area: record.irrigated.areaImprovedPasture, nFertiliser: record.irrigated.nFertiliserPasture, urea: record.irrigated.ureaFertiliserPasture, type: DairyProductionSystemTypeIrrigatedPastureTB.const }] : []),
      ...((record.irrigated.areaCropped > 0 || record.irrigated.nFertiliserCrops > 0 || record.irrigated.ureaFertiliserCrops > 0) && primaryProductionSystemType !== DairyProductionSystemTypeIrrigatedCropTB.const ? [{ area: record.irrigated.areaCropped, nFertiliser: record.irrigated.nFertiliserCrops, urea: record.irrigated.ureaFertiliserCrops, type: DairyProductionSystemTypeIrrigatedCropTB.const }] : [])
    ]
  }
}
export function transformDairyProductionSystemFormToRecord(inputs: DairyProductionSystemFormT, primaryProductionSystemType: DairyProductionSystemTypeT): DairyProductionSystemRecordT {
  const drylandPasture = primaryProductionSystemType === DairyProductionSystemTypeNonIrrigatedPastureTB.const
    ? inputs.primaryProductionSystemInputs
    : inputs.otherProductionSystemInputs?.find(s => s.type === DairyProductionSystemTypeNonIrrigatedPastureTB.const)
  const drylandCrop = primaryProductionSystemType === DairyProductionSystemTypeNonIrrigatedCropTB.const
    ? inputs.primaryProductionSystemInputs
    : inputs.otherProductionSystemInputs?.find(s => s.type === DairyProductionSystemTypeNonIrrigatedCropTB.const)
  const irrigatedPasture = primaryProductionSystemType === DairyProductionSystemTypeIrrigatedPastureTB.const
    ? inputs.primaryProductionSystemInputs
    : inputs.otherProductionSystemInputs?.find(s => s.type === DairyProductionSystemTypeIrrigatedPastureTB.const)
  const irrigatedCrops = primaryProductionSystemType === DairyProductionSystemTypeIrrigatedCropTB.const
    ? inputs.primaryProductionSystemInputs
    : inputs.otherProductionSystemInputs?.find(s => s.type === DairyProductionSystemTypeIrrigatedCropTB.const)

  return {
    limestone: inputs.limestone,
    singleSuperphosphate: inputs.singleSuperphosphate,
    herbicide: inputs.herbicide,
    herbicidePesticide: inputs.herbicidePesticide,
    // N.B. otherNFertilisers is non-null in the db, even though we don't require the user to submit any items
    // we keep it Type.Optional on DairyProductionSystemFormTB (more correct type as per intended user behaviour), and then if necessary add empty array here before hitting the API
    otherNFertilisers: inputs.otherNFertilisers ?? [],
    irrigated: {
      areaCropped: irrigatedCrops && irrigatedCrops.area ? irrigatedCrops.area : 0,
      areaImprovedPasture: irrigatedPasture && irrigatedPasture.area ? irrigatedPasture.area : 0,
      nFertiliserCrops: irrigatedCrops && irrigatedCrops.nFertiliser ? irrigatedCrops.nFertiliser : 0,
      nFertiliserPasture: irrigatedPasture && irrigatedPasture.nFertiliser ? irrigatedPasture.nFertiliser : 0,
      ureaFertiliserCrops: irrigatedCrops && irrigatedCrops.urea ? irrigatedCrops.urea : 0,
      ureaFertiliserPasture: irrigatedPasture && irrigatedPasture.urea ? irrigatedPasture.urea : 0
    },
    dryland: {
      areaCropped: drylandCrop && drylandCrop.area ? drylandCrop.area : 0,
      areaImprovedPasture: drylandPasture && drylandPasture.area ? drylandPasture.area : 0,
      nFertiliserCrops: drylandCrop && drylandCrop.nFertiliser ? drylandCrop.nFertiliser : 0,
      nFertiliserPasture: drylandPasture && drylandPasture.nFertiliser ? drylandPasture.nFertiliser : 0,
      ureaFertiliserCrops: drylandCrop && drylandCrop.urea ? drylandCrop.urea : 0,
      ureaFertiliserPasture: drylandPasture && drylandPasture.urea  ? drylandPasture.urea : 0
    }
  }
}

export const DairyCattleTransportTB = Type.Array(Type.Object({
  distance: NumberMinZeroDefaultUndefinedTB(),
  truckType: CattleTransportTruckTypeTB
}), { default: [] })
export type DairyCattleTransportT = Static<typeof DairyCattleTransportTB>

// bolt a Calves class on to the options
// TODO: is this the best approach for this? Why not a class in the db / GAF constants?
export const DairyCattleClassIdForProductionTB = Type.Union(
  DAIRY_CLASSES.map(c => Type.Literal(c.value)).concat(Type.Literal(7)),
)
export type DairyCattleClassIdForProductionT = Static<typeof DairyCattleClassIdForProductionTB>

export const DairyProductionTB = Type.Object({
  milkProduction: SeasonTB({ maximum: 50 }),
  sales: Type.Array(Type.Object({
    classId: DairyCattleClassIdForProductionTB,
    numberSold: NumberMinZeroDefaultUndefinedTB(),
    avgLiveweight: NumberMinZeroDefaultUndefinedTB(),
    stillWeaningAtSale: Type.Optional(Type.Boolean({ default: false })) // N.B. only a field if classId === 7 (which doesn't exist?)
  }), { default: [] })
})
export type DairyProductionT = Static<typeof DairyProductionTB>

export const ManureManagementCalculatorTB = Type.Object({
  hoursAtDairy: NumberMinZeroDefaultUndefinedTB({ minimum: 0, maximum: 24 }),
  daysAtDairy: NumberMinZeroDefaultUndefinedTB({ minimum: 0, maximum: 364 }),
  percentToPaddocks: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  percentToSump: NumberMinZeroDefaultUndefinedTB({ maximum: 100 }),
  isWastePretreated: Type.Boolean(),
  hoursAtFeedpad: NumberMinZeroDefaultUndefinedTB({ minimum: 0, maximum: 24 }),
  daysAtFeedpad: NumberMinZeroDefaultUndefinedTB({ minimum: 0, maximum: 364 }),
  wasteHandling: Type.Optional(Type.Number()),
  heifersHoursInYards: NumberMinZeroDefaultUndefinedTB({ minimum: 0, maximum: 24 }),
  heifersDaysInYards: NumberMinZeroDefaultUndefinedTB({ minimum: 0, maximum: 364 }),
  heiferWasteHandling: Type.Optional(Type.Number())
})
export type ManureManagementCalculatorT = Static<typeof ManureManagementCalculatorTB>

export const MilkProductionTB = SeasonTB()
export type MilkProductionT = Static<typeof MilkProductionTB>
